import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Button, Modal } from 'equasion-lib';
import { FiAlertTriangle, FiCheckCircle, FiChevronLeft } from 'react-icons/fi';
import FadeLoader from 'react-spinners/FadeLoader';
import { playFailTone, playSuccessTone } from '@/lib/scanning/feedback';
import SharedScanner from '@/lib/shared-coms/SharedScanner';
import './SalesOrderConvertToSerializedPickScan.scss';

function SalesOrderConvertToSerializedPickScan({
  salesOrder,
  bulkProductStatusUpdate,
  serializeUnserializedProducts,
  unserializedProduct,
  quantity
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateSubmitted, setUpdateSubmitted] = useState(false);
  const [scanLot, setScanLot] = useState(null);
  const [modalMessage, setModalMessage] = useState(null);
  const [scannedProducts, setScannedProducts] = useState([]);
  const navigate = useNavigate();
  const { id: salesOrderId, salesOrderItem } = salesOrder || {};
  const {
    quantity: salesOrderItemQuantity,
    serializedItemOfStatusCount,
    productPackagingKind: { id: productPackagingKindId, name, ndcPackageCode }
  } = salesOrderItem || {};
  const { lot, expiration, location } = unserializedProduct || {};
  const { id: locationId, name: locationName } = location || {};
  const { title, message } = modalMessage || {};

  const returnToPickScan = () =>
    navigate(
      `/sales-order-pick-scan?salesOrderId=${salesOrderId}&salesOrderItemId=${salesOrderItem.id}`
    );

  const returnToPicking = () => navigate(`/sales-order-picking/${salesOrderId}`);

  const submitBulkProductUpdate = async () => {
    try {
      setIsSubmitting(true);
      await bulkProductStatusUpdate({
        variables: {
          limit: parseInt(
            quantity || salesOrderItemQuantity - serializedItemOfStatusCount
          ),
          serialNumber: undefined,
          lot: lot.toUpperCase(),
          fromProductAttributes: {
            status: 15,
            productPackagingKindId,
            unserialized: true
          },
          toProductAttributes: {
            status: 16,
            salesOrderId
          }
        }
      });
      returnToPicking();
    } catch (e) {
      console.log(`ERROR UPDATING PRODUCTS: ${e}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitSerializeUnserializedProducts = async () => {
    try {
      setIsSubmitting(true);
      await serializeUnserializedProducts({
        variables: {
          unserializedProducts: scannedProducts.map(({ serialNumber }) => ({
            serialNumber
          })),
          locationId,
          lot,
          salesOrderId
        }
      });
      setUpdateSubmitted(true);
    } catch (e) {
      console.log(`ERROR UPDATING PRODUCTS: ${e}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const productScannedHandler = (product) => {
    const { serialNumber } = product || {};
    if (scannedProducts.some((p) => p.serialNumber === serialNumber)) {
      playFailTone();
      setModalMessage({
        title: 'Already Scanned Item',
        message: 'This item has already been scanned. Please scan a new, valid item.'
      });
      return;
    }
    const [productYear, productMonth, productDay] = product.expiration.split('-');
    const [expirationMonth, expirationDay, expirationYear] = expiration.split('/');
    const paddedProductMonth = productMonth.padStart(2, '0');
    const paddedExpirationMonth = expirationMonth.padStart(2, '0');
    // || expirationDateString !== productExpirationDateString
    if (
      product.lot !== lot ||
      expirationYear !== productYear ||
      paddedExpirationMonth !== paddedProductMonth
    ) {
      playFailTone();
      setModalMessage({
        title: 'Wrong Item Scanned',
        message: 'The scanned item does not match the expected Lot or Expiration Date.'
      });
      return;
    }
    playSuccessTone();
    setScannedProducts([...scannedProducts, product]);
  };

  return (
    <Page className="sales-order-convert-to-serialized-pick-scan action-page">
      <div className="action-top-bar">
        <div className="top">
          <Button
            type="secondary"
            iconBefore={<FiChevronLeft />}
            onClick={returnToPickScan}
          >
            Cancel
          </Button>
          <div>
            <h1>Convert to Serialized</h1>
            <span>Scan the 2D code found on the item</span>
          </div>
        </div>

        <div className="bottom">
          <div>
            <div>NDC: {ndcPackageCode}</div>
            <div>Location: {locationName}</div>
            <div>Expiration: {expiration}</div>
            <div>Lot: {lot}</div>
          </div>
          <div>
            <div>
              {scannedProducts.length} of {quantity} Scanned
            </div>
          </div>
        </div>
      </div>
      <div className="action-page-body">
        {!scannedProducts.length && (
          <Button type="secondary" onClick={submitBulkProductUpdate}>
            Continue Unserialized
          </Button>
        )}
        {!!scannedProducts.length && (
          <Button type="primary" onClick={submitSerializeUnserializedProducts}>
            Apply Serialization
          </Button>
        )}
        <SharedScanner
          scanLot={scanLot}
          setScanLot={setScanLot}
          handleScan={(result) => !!result.serialNumber && productScannedHandler(result)}
          hasManualEntry={false}
        />
        {!!isSubmitting && (
          <div
            className="blocking-spinner-container"
            onClick={(e) => e.stopPropagation()}
          >
            <FadeLoader color="var(--n0)" />
          </div>
        )}
      </div>

      <Modal
        className="sales-order-convert-to-serialized-pick-scan-modal"
        modalOpen={!!modalMessage}
        setModalOpen={() => setModalMessage(false)}
      >
        <div className="result-message">
          <FiAlertTriangle style={{ color: 'var(--red)' }} />
          <h1>{title}</h1>
          <p>{message}</p>
        </div>
        <div className="result-actions">
          <Button type="primary" fullWidth onClick={() => setModalMessage(null)}>
            Ok
          </Button>
        </div>
      </Modal>

      <div
        className={['result-panel', updateSubmitted && 'open'].filter(Boolean).join(' ')}
      >
        <div className="result-message">
          <FiCheckCircle style={{ color: 'var(--green)' }} />
          <h1>Items serialized successfully</h1>
          <p>Items are now serialized and must be treated as such.</p>
        </div>
        <div className="result-actions">
          <Button type="primary" fullWidth onClick={returnToPicking}>
            Done
          </Button>
        </div>
      </div>
    </Page>
  );
}

export default SalesOrderConvertToSerializedPickScan;
